// src/components/admin/UserLogs.jsx - FIXED VERSION  
import React, { useState, useEffect, useCallback } from "react";  
import { useSearchParams, useNavigate } from "react-router-dom";  
import { motion, AnimatePresence } from "framer-motion";  
import {  
  Search,  
  RefreshCw,  
  Download,  
  TrendingUp,  
  TrendingDown,  
  DollarSign,  
  X,  
  ArrowUpRight,  
  ArrowDownRight,  
  ChevronLeft,  
  ChevronRight,  
  FilterX,  
  BarChart3,  
  Activity,  
  Target,  
  FileSpreadsheet,  
  ArrowLeft,  
  Hash,  
  Users,  
  ChevronDown,  
} from "lucide-react";  
import api from "../../api";  
  
// ============================================  
// ANIMATED BACKGROUND  
// ============================================  
const AnimatedBackground = () => (  
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">  
    <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse" />  
    <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl animate-pulse delay-1000" />  
  </div>  
);  
  
// ============================================  
// STAT CARD COMPONENT  
// ============================================  
const StatCard = ({ icon: Icon, label, value, subValue, color, gradient }) => (  
  <motion.div  
    initial={{ opacity: 0, y: 20 }}  
    animate={{ opacity: 1, y: 0 }}  
    whileHover={{ y: -2 }}  
    className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-all"  
  >  
    <div className="flex items-start justify-between">  
      <div>  
        <p className="text-sm text-gray-500 mb-1">{label}</p>  
        <p className={`text-2xl font-bold ${color}`}>{value}</p>  
        {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}  
      </div>  
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>  
        <Icon size={22} className="text-white" />  
      </div>  
    </div>  
  </motion.div>  
);  
  
// ============================================  
// USER DROPDOWN SELECTOR  
// ============================================  
const UserDropdown = ({ users, selectedUserId, onSelectUser, loading, totalUsers }) => {  
  const [isOpen, setIsOpen] = useState(false);  
  const [searchTerm, setSearchTerm] = useState("");  
    
  const selectedUser = users.find(u => u.id === parseInt(selectedUserId));  
    
  const filteredUsers = users.filter(u =>   
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||  
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())  
  );  
  
  return (  
    <div className="relative">  
      <motion.button  
        whileHover={{ scale: 1.01 }}  
        whileTap={{ scale: 0.99 }}  
        onClick={() => setIsOpen(!isOpen)}  
        disabled={loading}  
        className={`flex items-center justify-between gap-3 w-full md:w-80 px-4 py-3.5 rounded-xl border transition-all ${  
          isOpen   
            ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'   
            : 'bg-white/5 border-white/10 text-white hover:bg-white/10'  
        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}  
      >  
        <div className="flex items-center gap-3">  
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${  
            selectedUser   
              ? 'bg-gradient-to-br from-amber-500 to-amber-600'   
              : 'bg-white/10'  
          }`}>  
            {selectedUser ? (  
              <span className="text-black font-bold text-sm">  
                {selectedUser.username?.charAt(0).toUpperCase()}  
              </span>  
            ) : (  
              <Users size={18} className="text-gray-400" />  
            )}  
          </div>  
          <div className="text-left">  
            <p className="font-medium">  
              {loading ? 'Loading users...' : selectedUser ? selectedUser.username : 'All Users'}  
            </p>  
            <p className="text-xs text-gray-500">  
              {loading ? 'Please wait' : selectedUser ? selectedUser.email : `${totalUsers} users available`}  
            </p>  
          </div>  
        </div>  
        <ChevronDown size={18} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />  
      </motion.button>  
  
      <AnimatePresence>  
        {isOpen && !loading && (  
          <>  
            <motion.div  
              initial={{ opacity: 0 }}  
              animate={{ opacity: 1 }}  
              exit={{ opacity: 0 }}  
              className="fixed inset-0 z-40"  
              onClick={() => setIsOpen(false)}  
            />  
            <motion.div  
              initial={{ opacity: 0, y: 10, scale: 0.95 }}  
              animate={{ opacity: 1, y: 0, scale: 1 }}  
              exit={{ opacity: 0, y: 10, scale: 0.95 }}  
              className="absolute top-full mt-2 left-0 w-full md:w-80 bg-[#1a1a1a]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden"  
            >  
              {/* Search */}  
              <div className="p-3 border-b border-white/10">  
                <div className="relative">  
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />  
                  <input  
                    type="text"  
                    value={searchTerm}  
                    onChange={(e) => setSearchTerm(e.target.value)}  
                    placeholder="Search users..."  
                    className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-amber-500/50"  
                    autoFocus  
                  />  
                </div>  
              </div>  
  
              {/* Options */}  
              <div className="max-h-64 overflow-y-auto">  
                {/* All Users Option */}  
                <button  
                  onClick={() => {  
                    onSelectUser('');  
                    setIsOpen(false);  
                    setSearchTerm('');  
                  }}  
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors ${  
                    !selectedUserId ? 'bg-amber-500/10 text-amber-400' : 'text-white'  
                  }`}  
                >  
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">  
                    <Users size={18} className="text-gray-400" />  
                  </div>  
                  <div className="text-left">  
                    <p className="font-medium">All Users</p>  
                    <p className="text-xs text-gray-500">View trades from all users</p>  
                  </div>  
                  {!selectedUserId && (  
                    <div className="ml-auto w-2 h-2 rounded-full bg-amber-500" />  
                  )}  
                </button>  
  
                {/* User List */}  
                {filteredUsers.length > 0 ? (  
                  filteredUsers.map(user => (  
                    <button  
                      key={user.id}  
                      onClick={() => {  
                        onSelectUser(user.id.toString());  
                        setIsOpen(false);  
                        setSearchTerm('');  
                      }}  
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors ${  
                        parseInt(selectedUserId) === user.id ? 'bg-amber-500/10 text-amber-400' : 'text-white'  
                      }`}  
                    >  
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${  
                        user.is_superuser   
                          ? 'bg-gradient-to-br from-purple-500 to-purple-600'   
                          : 'bg-gradient-to-br from-amber-500 to-amber-600'  
                      }`}>  
                        <span className="text-black font-bold text-sm">  
                          {user.username?.charAt(0).toUpperCase()}  
                        </span>  
                      </div>  
                      <div className="text-left flex-1 min-w-0">  
                        <p className="font-medium truncate">{user.username}</p>  
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>  
                      </div>  
                      {parseInt(selectedUserId) === user.id && (  
                        <div className="w-2 h-2 rounded-full bg-amber-500" />  
                      )}  
                    </button>  
                  ))  
                ) : (  
                  <div className="px-4 py-8 text-center text-gray-500">  
                    <p>{searchTerm ? 'No users match your search' : 'No users available'}</p>  
                  </div>  
                )}  
              </div>  
            </motion.div>  
          </>  
        )}  
      </AnimatePresence>  
    </div>  
  );  
};  
  
// ============================================  
// FILTER CHIP COMPONENT  
// ============================================  
const FilterChip = ({ label, value, onRemove, color = "amber" }) => {  
  const colors = {  
    amber: "bg-amber-500/20 text-amber-400 border-amber-500/30",  
    blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",  
    emerald: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",  
    red: "bg-red-500/20 text-red-400 border-red-500/30",  
    gray: "bg-gray-500/20 text-gray-400 border-gray-500/30",  
  };  
  
  return (  
    <motion.span  
      initial={{ scale: 0.9, opacity: 0 }}  
      animate={{ scale: 1, opacity: 1 }}  
      exit={{ scale: 0.9, opacity: 0 }}  
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border ${colors[color]}`}  
    >  
      {label}: {value}  
      <button onClick={onRemove} className="hover:bg-white/20 rounded-full p-0.5 transition-colors">  
        <X size={12} />  
      </button>  
    </motion.span>  
  );  
};  
  
// ============================================  
// TRADE ROW COMPONENT  
// ============================================  
const TradeRow = ({ trade, index }) => {  
  const pnl = parseFloat(trade.pnl || 0);  
  const isProfitable = pnl > 0;  
  const isLoss = pnl < 0;  
  const isBuy = trade.side?.toLowerCase() === "buy";  
  
  return (  
    <motion.tr  
      initial={{ opacity: 0, x: -20 }}  
      animate={{ opacity: 1, x: 0 }}  
      transition={{ delay: index * 0.02 }}  
      className="border-b border-white/5 hover:bg-white/5 transition-colors group"  
    >  
      {/* User */}  
      <td className="p-4">  
        <div className="flex items-center gap-3">  
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 flex items-center justify-center border border-amber-500/30">  
            <span className="text-amber-400 font-bold text-sm">  
              {trade.user_username?.charAt(0).toUpperCase() || '#'}  
            </span>  
          </div>  
          <div>  
            <p className="text-sm font-medium text-white group-hover:text-amber-400 transition-colors">  
              {trade.user_username || `User #${trade.user_id}`}  
            </p>  
            <p className="text-xs text-gray-500">ID: {trade.user_id}</p>  
          </div>  
        </div>  
      </td>  
  
      {/* Trade ID */}  
      <td className="p-4">  
        <div className="flex items-center gap-2">  
          <Hash size={14} className="text-gray-500" />  
          <span className="text-sm text-gray-400 font-mono">{trade.trade_id || trade.id || '-'}</span>  
        </div>  
      </td>  
  
      {/* Symbol */}  
      <td className="p-4">  
        <div className="flex items-center gap-2">  
          <div className={`w-2 h-2 rounded-full ${isBuy ? 'bg-emerald-400' : 'bg-red-400'}`} />  
          <span className="text-sm font-semibold text-white">{trade.symbol}</span>  
        </div>  
      </td>  
  
      {/* Side */}  
      <td className="p-4">  
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg ${  
          isBuy   
            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'   
            : 'bg-red-500/20 text-red-400 border border-red-500/30'  
        }`}>  
          {isBuy ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}  
          {trade.side?.toUpperCase()}  
        </span>  
      </td>  
  
      {/* Entry Price */}  
      <td className="p-4">  
        <span className="text-sm text-white font-mono">  
          ${parseFloat(trade.entry || 0).toFixed(4)}  
        </span>  
      </td>  
  
      {/* Exit Price */}  
      <td className="p-4">  
        <span className="text-sm text-gray-400 font-mono">  
          {trade.exit_price ? `$${parseFloat(trade.exit_price).toFixed(4)}` : '-'}  
        </span>  
      </td>  
  
      {/* Quantity */}  
      <td className="p-4">  
        <span className="text-sm text-gray-400 font-mono">  
          {trade.quantity || trade.size || '-'}  
        </span>  
      </td>  
  
      {/* PNL */}  
      <td className="p-4">  
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg ${  
          isProfitable   
            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'   
            : isLoss  
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'  
              : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'  
        }`}>  
          {isProfitable ? <TrendingUp size={12} /> : isLoss ? <TrendingDown size={12} /> : null}  
          {isProfitable ? '+' : ''}{pnl.toFixed(2)} USDT  
        </span>  
      </td>  
  
      {/* Timestamp */}  
      <td className="p-4">  
        <div className="flex flex-col">  
          <span className="text-sm text-white">  
            {new Date(trade.timestamp).toLocaleDateString()}  
          </span>  
          <span className="text-xs text-gray-500">  
            {new Date(trade.timestamp).toLocaleTimeString()}  
          </span>  
        </div>  
      </td>  
    </motion.tr>  
  );  
};  
  
// ============================================  
// PAGINATION COMPONENT  
// ============================================  
const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) => {  
  const startItem = (currentPage - 1) * itemsPerPage + 1;  
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);  
  
  const getPageNumbers = () => {  
    const pages = [];  
    const maxVisible = 5;  
      
    if (totalPages <= maxVisible) {  
      for (let i = 1; i <= totalPages; i++) pages.push(i);  
    } else if (currentPage <= 3) {  
      for (let i = 1; i <= maxVisible; i++) pages.push(i);  
    } else if (currentPage >= totalPages - 2) {  
      for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) pages.push(i);  
    } else {  
      for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);  
    }  
      
    return pages;  
  };  
  
  return (  
    <motion.div   
      initial={{ opacity: 0 }}  
      animate={{ opacity: 1 }}  
      className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-white/10 rounded-2xl"  
    >  
      <p className="text-sm text-gray-400">  
        Showing <span className="text-white font-medium">{startItem.toLocaleString()}</span> to{" "}  
        <span className="text-white font-medium">{endItem.toLocaleString()}</span> of{" "}  
        <span className="text-white font-medium">{totalItems.toLocaleString()}</span> trades  
      </p>  
        
      <div className="flex items-center gap-2">  
        {currentPage > 3 && totalPages > 5 && (  
          <>  
            <motion.button  
              whileHover={{ scale: 1.05 }}  
              whileTap={{ scale: 0.95 }}  
              onClick={() => onPageChange(1)}  
              className="w-10 h-10 rounded-xl text-sm font-semibold text-gray-400 hover:bg-white/10 hover:text-white transition-all"  
            >  
              1  
            </motion.button>  
            <span className="text-gray-600 px-1">...</span>  
          </>  
        )}  
  
        <motion.button  
          whileHover={{ scale: 1.05 }}  
          whileTap={{ scale: 0.95 }}  
          onClick={() => onPageChange(currentPage - 1)}  
          disabled={currentPage === 1}  
          className="p-2.5 rounded-xl border border-white/10 hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"  
        >  
          <ChevronLeft size={16} className="text-gray-400" />  
        </motion.button>  
          
        {getPageNumbers().map((page) => (  
          <motion.button  
            key={page}  
            whileHover={{ scale: 1.05 }}  
            whileTap={{ scale: 0.95 }}  
            onClick={() => onPageChange(page)}  
            className={`w-10 h-10 rounded-xl text-sm font-semibold transition-all ${  
              currentPage === page  
                ? "bg-gradient-to-r from-amber-500 to-amber-600 text-black shadow-lg shadow-amber-500/30"  
                : "text-gray-400 hover:bg-white/10 hover:text-white"  
            }`}  
          >  
            {page}  
          </motion.button>  
        ))}  
          
        <motion.button  
          whileHover={{ scale: 1.05 }}  
          whileTap={{ scale: 0.95 }}  
          onClick={() => onPageChange(currentPage + 1)}  
          disabled={currentPage === totalPages}  
          className="p-2.5 rounded-xl border border-white/10 hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"  
        >  
          <ChevronRight size={16} className="text-gray-400" />  
        </motion.button>  
  
        {currentPage < totalPages - 2 && totalPages > 5 && (  
          <>  
            <span className="text-gray-600 px-1">...</span>  
            <motion.button  
              whileHover={{ scale: 1.05 }}  
              whileTap={{ scale: 0.95 }}  
              onClick={() => onPageChange(totalPages)}  
              className="w-10 h-10 rounded-xl text-sm font-semibold text-gray-400 hover:bg-white/10 hover:text-white transition-all"  
            >  
              {totalPages}  
            </motion.button>  
          </>  
        )}  
      </div>  
    </motion.div>  
  );  
};  
  
// ============================================  
// LOADING STATE  
// ============================================  
const LoadingState = () => (  
  <div className="flex flex-col items-center justify-center py-20">  
    <div className="w-16 h-16 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />  
    <p className="text-gray-400 mt-6 font-medium">Loading trade logs...</p>  
  </div>  
);  
  
// ============================================  
// EMPTY STATE  
// ============================================  
const EmptyState = ({ hasFilters, onClearFilters }) => (  
  <motion.div   
    initial={{ opacity: 0, y: 20 }}  
    animate={{ opacity: 1, y: 0 }}  
    className="flex flex-col items-center justify-center py-20 px-4"  
  >  
    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-gray-500/20 to-gray-600/20 flex items-center justify-center mb-6">  
      <FileSpreadsheet size={48} className="text-gray-500" />  
    </div>  
    <h3 className="text-xl font-bold text-white mb-2">No Trades Found</h3>  
    <p className="text-gray-400 text-center max-w-md mb-6">  
      {hasFilters   
        ? "No trades match your current filters."  
        : "No trades have been executed yet."  
      }  
    </p>  
    {hasFilters && (  
      <motion.button  
        whileHover={{ scale: 1.02 }}  
        whileTap={{ scale: 0.98 }}  
        onClick={onClearFilters}  
        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-xl"  
      >  
        <FilterX size={18} />  
        Clear All Filters  
      </motion.button>  
    )}  
  </motion.div>  
);  
  
// ============================================  
// MAIN USER LOGS COMPONENT  
// ============================================  
const UserLogs = () => {  
  const navigate = useNavigate();  
  const [searchParams, setSearchParams] = useSearchParams();  
    
  // State  
  const [trades, setTrades] = useState([]);  
  const [loading, setLoading] = useState(true);  
  const [error, setError] = useState(null);  
    
  // Users for dropdown - fetch ALL users  
  const [users, setUsers] = useState([]);  
  const [usersLoading, setUsersLoading] = useState(true);  
  const [totalUsersCount, setTotalUsersCount] = useState(0);  
    
  // Symbols for filter  
  const [symbols, setSymbols] = useState([]);  
    
  // Filters  
  const [filters, setFilters] = useState({  
    userId: searchParams.get("user_id") || '',  
    symbol: searchParams.get("symbol") || '',  
    search: '',  
  });  
    
  // Selected user info  
  const [selectedUsername, setSelectedUsername] = useState(searchParams.get("username") || '');  
    
  // Pagination from backend  
  const [pagination, setPagination] = useState({  
    currentPage: 1,  
    totalPages: 1,  
    totalItems: 0,  
    limit: 50,  
  });  
    
  // Stats  
  const [stats, setStats] = useState({  
    totalTrades: 0,  
    totalPnl: 0,  
    winRate: 0,  
    avgProfit: 0,  
    avgLoss: 0,  
  });  
  
  const token = localStorage.getItem("access_token");  
  
  useEffect(() => {  
    const fetchUsers = async () => {  
      try {  
        setUsersLoading(true);  
        const res = await api.get(`/admin/users/lookup?limit=1000`, {  
          headers: { Authorization: `Bearer ${token}` },  
        });  
        const list = res.data || [];  
        setUsers(list);  
        setTotalUsersCount(list.length);  
        // symbols will come from trades payload instead  
      } catch (err) {  
        console.error("Failed to fetch users:", err);  
      } finally {  
        setUsersLoading(false);  
      }  
    };  
    fetchUsers();  
  }, [token]);  
  // Fetch trades with pagination  
  const fetchTrades = useCallback(async (page = 1) => {  
    try {  
      setLoading(true);  
      setError(null);  
        
      // Build query params matching your backend  
      const params = new URLSearchParams();  
      params.append("page", page.toString());  
      params.append("limit", "50");  
        
      if (filters.userId) {  
        params.append("user_id", filters.userId);  
      }  
      if (filters.symbol) {  
        params.append("symbol", filters.symbol);  
      }  
        
      console.log("Fetching trades with params:", params.toString());  
        
      const response = await api.get(`/logs/all_trade_logs?${params.toString()}`, {  
        headers: { Authorization: `Bearer ${token}` },  
      });  
        
      console.log("Trades API response:", response.data);  
        
      // Your backend returns: { total, page, pages, limit, trades }  
      const { total, page: currentPage, pages, limit, trades: tradesData } = response.data;  
        
      setTrades(tradesData || []);  
      setPagination({  
        currentPage: currentPage || 1,  
        totalPages: pages || 1,  
        totalItems: total || 0,  
        limit: limit || 50,  
      });  
        
      // Extract unique symbols for filter dropdown  
      const uniqueSymbols = [...new Set(tradesData?.map(t => t.symbol).filter(Boolean) || [])];  
      setSymbols(prev => {  
        const combined = [...new Set([...prev, ...uniqueSymbols])];  
        return combined;  
      });  
        
    } catch (err) {  
      console.error("Error fetching trades:", err);  
      setError(`Failed to load trade logs: ${err.response?.data?.detail || err.message}`);  
    } finally {  
      setLoading(false);  
    }  
  }, [token, filters.userId, filters.symbol]);  
  
  const fetchStats = useCallback(() => {  
    // Derive from current page of trades — accurate enough for header display  
    const closed = trades.filter((t) => t.pnl != null);  
    const wins = closed.filter((t) => t.pnl > 0);  
    const totalPnl = trades.reduce((s, t) => s + (parseFloat(t.pnl) || 0), 0);  
    setStats({  
      totalTrades: pagination.totalItems || 0,  
      totalPnl,  
      winRate: closed.length ? (wins.length / closed.length) * 100 : 0,  
      avgProfit: 0,  
      avgLoss: 0,  
    });  
  }, [trades, pagination.totalItems]);  
  
  // Initial fetch  
  useEffect(() => {  
    fetchTrades(1);  
  }, [fetchTrades]);  
  
  // Update stats when users or filters change  
  useEffect(() => {  
    if (users.length > 0) {  
      fetchStats();  
    }  
  }, [users, filters.userId, fetchStats]);  
  
  // Update URL and username when user filter changes  
  useEffect(() => {  
    const params = new URLSearchParams();  
    if (filters.userId) {  
      params.set("user_id", filters.userId);  
      const user = users.find(u => u.id === parseInt(filters.userId));  
      if (user) {  
        params.set("username", user.username);  
        setSelectedUsername(user.username);  
      }  
    } else {  
      setSelectedUsername('');  
    }  
    if (filters.symbol) {  
      params.set("symbol", filters.symbol);  
    }  
    setSearchParams(params);  
  }, [filters.userId, filters.symbol, users, setSearchParams]);  
  
  // Handle user selection from dropdown  
  const handleUserSelect = (userId) => {  
    setFilters(prev => ({ ...prev, userId }));  
  };  
  
  // Re-fetch trades when filters change  
  useEffect(() => {  
    fetchTrades(1);  
  }, [filters.userId, filters.symbol]);  
  
  // Handle page change  
  const handlePageChange = (page) => {  
    fetchTrades(page);  
    window.scrollTo({ top: 0, behavior: 'smooth' });  
  };  
  
  // Clear filters  
  const clearFilters = () => {  
    setFilters({  
      userId: '',  
      symbol: '',  
      search: '',  
    });  
  };  
  
  // Check if any filters are active  
  const hasActiveFilters = filters.userId || filters.symbol || filters.search;  
  
  // Filter trades by search (client-side for current page)  
  const displayedTrades = filters.search   
    ? trades.filter(t =>   
        t.symbol?.toLowerCase().includes(filters.search.toLowerCase()) ||  
        t.trade_id?.toString().includes(filters.search) ||  
        t.user_username?.toLowerCase().includes(filters.search.toLowerCase())  
      )  
    : trades;  
  
  // Export CSV - uses backend CSV endpoint  
  const handleExportCSV = async () => {  
    try {  
      const params = new URLSearchParams();  
      params.append("csv", "true");  
      if (filters.userId) params.append("user_id", filters.userId);  
      if (filters.symbol) params.append("symbol", filters.symbol);  
        
      const response = await api.get(`/logs/all_trade_logs?${params.toString()}`, {  
        headers: { Authorization: `Bearer ${token}` },  
        responseType: 'blob',  
      });  
        
      const url = window.URL.createObjectURL(new Blob([response.data]));  
      const link = document.createElement('a');  
      link.href = url;  
      const filename = filters.userId   
        ? `${selectedUsername || 'user'}_trades_${new Date().toISOString().split('T')[0]}.csv`  
        : `all_trades_${new Date().toISOString().split('T')[0]}.csv`;  
      link.setAttribute('download', filename);  
      document.body.appendChild(link);  
      link.click();  
      link.remove();  
      window.URL.revokeObjectURL(url);  
    } catch (err) {  
      console.error("Failed to export CSV:", err);  
      alert("Failed to export trades. Please try again.");  
    }  
  };  
  
  return (  
    <div className="min-h-screen relative">  
      <AnimatedBackground />  
        
      <div className="space-y-6 relative z-10">  
        {/* Header */}  
        <motion.div   
          initial={{ opacity: 0, y: -20 }}  
          animate={{ opacity: 1, y: 0 }}  
          className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"  
        >  
          <div className="flex items-center gap-4">  
            <motion.button  
              whileHover={{ scale: 1.05, x: -2 }}  
              whileTap={{ scale: 0.95 }}  
              onClick={() => navigate('/admin/users')}  
              className="p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all"  
            >  
              <ArrowLeft size={20} className="text-gray-400" />  
            </motion.button>  
            <div>  
              <h1 className="text-4xl font-bold text-white flex items-center gap-3">  
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">  
                  <BarChart3 size={24} className="text-white" />  
                </div>  
                Trade Logs  
              </h1>  
              <p className="text-gray-400 mt-2">  
                {selectedUsername   
                  ? `Viewing trades for ${selectedUsername}`  
                  : 'View and analyze all trading activity'  
                }  
              </p>  
            </div>  
          </div>  
            
          <div className="flex items-center gap-3">  
            <motion.button  
              whileHover={{ scale: 1.05 }}  
              whileTap={{ scale: 0.95 }}  
              onClick={() => fetchTrades(pagination.currentPage)}  
              disabled={loading}  
              className="p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all disabled:opacity-50"  
              title="Refresh"  
            >  
              <RefreshCw size={20} className={`text-gray-400 ${loading ? "animate-spin" : ""}`} />  
            </motion.button>  
              
            <motion.button  
              whileHover={{ scale: 1.05 }}  
              whileTap={{ scale: 0.95 }}  
              onClick={handleExportCSV}  
              disabled={pagination.totalItems === 0}  
              className="flex items-center gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all text-gray-400 hover:text-white disabled:opacity-50"  
            >  
              <Download size={18} />  
              <span className="hidden sm:inline">Export CSV</span>  
            </motion.button>  
          </div>  
        </motion.div>  
  
        {/* Stats Summary */}  
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">  
          <StatCard  
            icon={Activity}  
            label="Total Trades"  
            value={stats.totalTrades.toLocaleString()}  
            color="text-white"  
            gradient="from-amber-500 to-amber-600"  
          />  
          <StatCard  
            icon={DollarSign}  
            label="Total P&L"  
            value={`${stats.totalPnl >= 0 ? '+' : ''}${stats.totalPnl.toFixed(2)}`}  
            subValue="USDT"  
            color={stats.totalPnl >= 0 ? "text-emerald-400" : "text-red-400"}  
            gradient={stats.totalPnl >= 0 ? "from-emerald-500 to-emerald-600" : "from-red-500 to-red-600"}  
          />  
          <StatCard  
            icon={Target}  
            label="Win Rate"  
            value={`${stats.winRate.toFixed(1)}%`}  
            color={stats.winRate >= 50 ? "text-emerald-400" : "text-amber-400"}  
            gradient="from-blue-500 to-blue-600"  
          />  

          <StatCard  
            icon={Users}  
            label="Users total"  
            value={totalUsersCount.toString()}  
            subValue="registered"  
            color="text-white"  
            gradient="from-purple-500 to-purple-600"  
          />  
        </div>  
  
        {/* Filters Bar */}  
        <motion.div   
          initial={{ opacity: 0, y: -10 }}  
          animate={{ opacity: 1, y: 0 }}  
          className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-white/10 rounded-2xl p-5"  
        >  
          <div className="flex flex-col lg:flex-row gap-4">  
            {/* User Dropdown */}  
            <UserDropdown  
              users={users}  
              selectedUserId={filters.userId}  
              onSelectUser={handleUserSelect}  
              loading={usersLoading}  
              totalUsers={totalUsersCount}  
            />  
  
            {/* Search Input */}  
            <div className="flex-1 relative">  
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />  
              <input  
                type="text"  
                value={filters.search}  
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}  
                placeholder="Search current page by symbol or trade ID..."  
                className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 transition-all"  
              />  
              {filters.search && (  
                <button  
                  onClick={() => setFilters({ ...filters, search: "" })}  
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"  
                >  
                  <X size={16} />  
                </button>  
              )}  
            </div>  
  
            {/* Symbol Filter */}  
            {symbols.length > 0 && (  
              <select  
                value={filters.symbol}  
                onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}  
                className="px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50 transition-all appearance-none cursor-pointer min-w-[160px]"  
                style={{   
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%236b7280\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")',   
                  backgroundRepeat: 'no-repeat',   
                  backgroundPosition: 'right 12px center',   
                  backgroundSize: '16px',   
                  paddingRight: '40px'   
                }}  
              >  
                <option value="">All Symbols</option>  
                {symbols.map(symbol => (  
                  <option key={symbol} value={symbol}>{symbol}</option>  
                ))}  
              </select>  
            )}  
          </div>  
  
          {/* Active Filters Display */}  
          {hasActiveFilters && (  
            <motion.div   
              initial={{ opacity: 0, height: 0 }}  
              animate={{ opacity: 1, height: "auto" }}  
              className="flex items-center gap-3 mt-4 pt-4 border-t border-white/10 flex-wrap"  
            >  
              <span className="text-xs text-gray-500">Active filters:</span>  
              <div className="flex flex-wrap gap-2">  
                <AnimatePresence>  
                  {filters.userId && (  
                    <FilterChip  
                      label="User"  
                      value={selectedUsername || `#${filters.userId}`}  
                      onRemove={() => setFilters({ ...filters, userId: '' })}  
                      color="amber"  
                    />  
                  )}  
                  {filters.symbol && (  
                    <FilterChip  
                      label="Symbol"  
                      value={filters.symbol}  
                      onRemove={() => setFilters({ ...filters, symbol: '' })}  
                      color="blue"  
                    />  
                  )}  
                  {filters.search && (  
                    <FilterChip  
                      label="Search"  
                      value={filters.search}  
                      onRemove={() => setFilters({ ...filters, search: '' })}  
                      color="gray"  
                    />  
                  )}  
                </AnimatePresence>  
              </div>  
              <button  
                onClick={clearFilters}  
                className="ml-auto text-xs text-gray-500 hover:text-white transition-colors flex items-center gap-1"  
              >  
                <FilterX size={12} />  
                Clear all  
              </button>  
            </motion.div>  
          )}  
        </motion.div>  
  
        {/* Content Area */}  
        {loading ? (  
          <LoadingState />  
        ) : error ? (  
          <motion.div   
            initial={{ opacity: 0, y: 20 }}  
            animate={{ opacity: 1, y: 0 }}  
            className="flex flex-col items-center justify-center py-20 px-4"  
          >  
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-red-500/20 to-red-600/20 flex items-center justify-center mb-6">  
              <X size={48} className="text-red-500" />  
            </div>  
            <h3 className="text-xl font-bold text-white mb-2">Failed to Load Trades</h3>  
            <p className="text-gray-400 text-center max-w-md mb-6">{error}</p>  
            <motion.button  
              whileHover={{ scale: 1.02 }}  
              whileTap={{ scale: 0.98 }}  
              onClick={() => fetchTrades(1)}  
              className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-xl shadow-lg shadow-amber-500/30 flex items-center gap-2"  
            >  
              <RefreshCw size={18} />  
              Try Again  
            </motion.button>  
          </motion.div>  
        ) : displayedTrades.length === 0 ? (  
          <EmptyState hasFilters={hasActiveFilters} onClearFilters={clearFilters} />  
        ) : (  
          <>  
            {/* Trades Table */}  
            <motion.div   
              initial={{ opacity: 0 }}  
              animate={{ opacity: 1 }}  
              className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden"  
            >  
              <div className="overflow-x-auto">  
                <table className="w-full">  
                  <thead>  
                    <tr className="border-b border-white/10 bg-white/5">  
                      <th className="p-4 text-left">  
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">User</span>  
                      </th>  
                      <th className="p-4 text-left">  
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Trade ID</span>  
                      </th>  
                      <th className="p-4 text-left">  
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Symbol</span>  
                      </th>  
                      <th className="p-4 text-left">  
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Side</span>  
                      </th>  
                      <th className="p-4 text-left">  
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Entry</span>  
                      </th>  
                      <th className="p-4 text-left">  
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Exit</span>  
                      </th>  
                      <th className="p-4 text-left">  
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</span>  
                      </th>  
                      <th className="p-4 text-left">  
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">P&L</span>  
                      </th>  
                      <th className="p-4 text-left">  
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Time</span>  
                      </th>  
                    </tr>  
                  </thead>  
                  <tbody>  
                    {displayedTrades.map((trade, index) => (  
                      <TradeRow key={trade.id || trade.trade_id || index} trade={trade} index={index} />  
                    ))}  
                  </tbody>  
                </table>  
              </div>  
            </motion.div>  
  
            {/* Pagination */}  
            {pagination.totalPages > 1 && (  
              <Pagination  
                currentPage={pagination.currentPage}  
                totalPages={pagination.totalPages}  
                totalItems={pagination.totalItems}  
                itemsPerPage={pagination.limit}  
                onPageChange={handlePageChange}  
              />  
            )}  
          </>  
        )}  
      </div>  
    </div>  
  );  
};  
  
export default UserLogs;  